import React, { useRef, useState, useEffect } from "react"
import ReactDOM from "react-dom"
import { VariantReader } from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"

const Tooltip = ({
  children,
  text,
  content,
  position = "top",
  zIndex = 9999,
  display = "inline-block", // Add display prop with default
  variant = "light",
  styleOverrides,
  className = "",
  isOpen
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipStyles, setTooltipStyles] = useState({})
  const containerRef = useRef()
  const tooltipRef = useRef()

  const shouldShowTooltip = isOpen !== undefined ? isOpen : isVisible

  const variants = VariantReader.useVariant({
    componentName: "Tooltip",
    variant,
    styleOverrides,
  })

  useEffect(() => {
    if (shouldShowTooltip) {
      const containerRect = containerRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()

      let top, left

      switch (position) {
        case "top":
          top = containerRect.top - tooltipRect.height - 8 // 8px for margin
          left =
            containerRect.left + containerRect.width / 2 - tooltipRect.width / 2
          break
        case "bottom":
          top = containerRect.bottom + 8
          left =
            containerRect.left + containerRect.width / 2 - tooltipRect.width / 2
          break
        case "left":
          top =
            containerRect.top +
            containerRect.height / 2 -
            tooltipRect.height / 2
          left = containerRect.left - tooltipRect.width - 8
          break
        case "right":
          top =
            containerRect.top +
            containerRect.height / 2 -
            tooltipRect.height / 2
          left = containerRect.right + 8
          break
        default:
          top = containerRect.bottom + 8
          left =
            containerRect.left + containerRect.width / 2 - tooltipRect.width / 2
      }

      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      if (top < 0) top = 8
      if (left < 0) left = 8
      if (top + tooltipRect.height > viewportHeight)
        top = viewportHeight - tooltipRect.height - 8
      if (left + tooltipRect.width > viewportWidth)
        left = viewportWidth - tooltipRect.width - 8

      setTooltipStyles({ top: `${top}px`, left: `${left}px` })
    }
  }, [shouldShowTooltip, position])

  useEffect(() => {
    return () => {
      setIsVisible(false)
    }
  }, [])

  const tooltipElement = (
    <div
      ref={tooltipRef}
      style={{
        ...tooltipStyles,
        whiteSpace: "pre-wrap",
        zIndex: zIndex,
        pointerEvents: "none" // Prevent tooltip from interfering with mouse events
      }}
      className={twMerge("fixed w-max", variants.tooltip, className)}
    >
      {content ? content : text}
    </div>
  )

  // Use dynamic className based on display prop
  const wrapperClassName = display === "block" ? "relative block w-full" : "relative inline-block"

  return (
    <div className={wrapperClassName}>
      <div
        ref={containerRef}
        className="hover:relative w-full" // Add w-full for block mode
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {shouldShowTooltip && ReactDOM.createPortal(tooltipElement, document.body)}
    </div>
  )
}

export default Tooltip